'use client'

/**
 * ImagePositionEditor — r19.43.10 (Stephan-Spec 2026-06-24).
 *
 * Modal-Editor zum Positionieren + Zoomen eines hochgeladenen Modell-Fotos
 * im Hangar. Stephan-Spec: "wenn der pilot sein foto zum modell hochlädt
 * muss es positioniert und gezoomt werden können, so wie das piloten
 * avatar bild" + "auf ein einheitliches format müsste positioniert und
 * gezoomt werden können".
 *
 * Format der gespeicherten Position:
 *   { zoom: 1.0–4.0, x: -50…+50, y: -50…+50 }
 *   zoom=1.0 + x=0 + y=0 = object-cover-Center (Standard).
 *   x/y in Prozent vom Container — negativ = links/oben, positiv = rechts/unten.
 *
 * Bedienung:
 *   - Maus-Drag im Vorschau-Rahmen = pannen
 *   - Mausrad / Pinch = zoomen
 *   - Slider für Zoom unten
 *   - Reset-Button setzt auf 1.0 / 0 / 0
 */
import { useState, useRef, useEffect } from 'react'
import { X, RotateCcw, ZoomIn, ZoomOut, Check, Move } from 'lucide-react'

export interface ImagePosition {
  zoom: number
  x: number
  y: number
}

interface Props {
  imageUrl: string
  initial: ImagePosition | null
  /** Card-Verhältnis (Galerie etc.) — 4:3 default. Hero ist quadratischer. */
  aspectRatio?: number
  onSave: (pos: ImagePosition) => Promise<void> | void
  onCancel: () => void
}

const DEFAULT_POS: ImagePosition = { zoom: 1, x: 0, y: 0 }
const MIN_ZOOM = 1
const MAX_ZOOM = 4

export function ImagePositionEditor({ imageUrl, initial, aspectRatio = 4 / 3, onSave, onCancel }: Props) {
  const [pos, setPos] = useState<ImagePosition>(initial || DEFAULT_POS)
  const [saving, setSaving] = useState(false)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null)

  // Mouse-Drag
  useEffect(() => {
    const move = (e: MouseEvent) => {
      const d = dragRef.current
      if (!d) return
      const dx = e.clientX - d.startX
      const dy = e.clientY - d.startY
      const cw = containerRef.current?.clientWidth ?? 1
      const ch = containerRef.current?.clientHeight ?? 1
      // dx/cw = % Bewegung relativ zur Container-Breite
      const newX = Math.max(-50, Math.min(50, d.baseX + (dx / cw) * 100))
      const newY = Math.max(-50, Math.min(50, d.baseY + (dy / ch) * 100))
      setPos((p) => ({ ...p, x: newX, y: newY }))
    }
    const up = () => { dragRef.current = null }
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    return () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
    }
  }, [])

  const onMouseDown = (e: React.MouseEvent) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y }
  }

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -0.1 : 0.1
    setPos((p) => ({ ...p, zoom: Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, p.zoom + delta)) }))
  }

  const handleReset = () => setPos(DEFAULT_POS)

  const handleSave = async () => {
    setSaving(true)
    try {
      await onSave(pos)
    } finally {
      setSaving(false)
    }
  }

  // CSS transform aus position
  const transform = `translate(${pos.x}%, ${pos.y}%) scale(${pos.zoom})`

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={onCancel}
    >
      <div
        className="relative rounded-2xl max-w-2xl w-full"
        style={{ background: 'var(--card)', border: '1px solid var(--border)', padding: 20 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-black uppercase tracking-wider text-sm" style={{ color: 'var(--text)' }}>
            Foto positionieren + zoomen
          </h2>
          <button onClick={onCancel} className="text-[var(--muted)] hover:text-[var(--text)]" aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-xs text-[var(--muted)] mb-4 leading-relaxed">
          <Move className="inline h-3 w-3 mr-1" /> Mit der Maus ziehen zum Verschieben.
          {' '}Mausrad für Zoom. Klick auf Speichern wenn alles passt — das Foto wird
          in der Galerie, im Hero und im Hangar-Thumbnail mit dieser Position angezeigt.
        </p>

        {/* Vorschau-Rahmen */}
        <div
          ref={containerRef}
          className="relative overflow-hidden rounded-xl mb-4 cursor-grab active:cursor-grabbing select-none"
          style={{
            background: 'var(--award-ink-2)',
            border: '1px solid var(--border)',
            aspectRatio,
            userSelect: 'none',
          }}
          onMouseDown={onMouseDown}
          onWheel={onWheel}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageUrl}
            alt="Foto-Positionierung"
            className="w-full h-full object-cover pointer-events-none"
            style={{
              transform,
              transformOrigin: 'center center',
            }}
            draggable={false}
          />
          {/* Crop-Marker — Mitte */}
          <div
            className="absolute inset-0 pointer-events-none flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.04)' }}
          >
            <div className="border border-white/30 w-10 h-10 rounded-full" />
          </div>
        </div>

        {/* Zoom-Slider */}
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => setPos((p) => ({ ...p, zoom: Math.max(MIN_ZOOM, p.zoom - 0.1) }))}
            className="p-1.5 rounded-md hover:bg-[var(--award-ink-2)]"
            aria-label="Verkleinern"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <input
            type="range"
            min={MIN_ZOOM}
            max={MAX_ZOOM}
            step={0.05}
            value={pos.zoom}
            onChange={(e) => setPos((p) => ({ ...p, zoom: parseFloat(e.target.value) }))}
            className="flex-1"
            style={{ accentColor: 'var(--accent)' }}
          />
          <button
            onClick={() => setPos((p) => ({ ...p, zoom: Math.min(MAX_ZOOM, p.zoom + 0.1) }))}
            className="p-1.5 rounded-md hover:bg-[var(--award-ink-2)]"
            aria-label="Vergrößern"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <span className="text-xs font-mono tabular-nums opacity-60" style={{ minWidth: 38 }}>
            {pos.zoom.toFixed(2)}×
          </span>
        </div>

        {/* Action-Buttons */}
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={handleReset}
            className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-md hover:bg-[var(--award-ink-2)] inline-flex items-center gap-1.5"
            style={{ color: 'var(--muted)' }}
          >
            <RotateCcw className="h-3 w-3" /> Zurücksetzen
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              disabled={saving}
              className="text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-md hover:bg-[var(--award-ink-2)]"
              style={{ color: 'var(--muted)' }}
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="wertung-tab-pill inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-xs font-bold uppercase tracking-widest disabled:opacity-50"
              data-state="active"
            >
              <Check className="h-3 w-3" /> {saving ? 'Speichern …' : 'Speichern'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Liest eine gespeicherte ImagePosition aus dem JSON-Feld + liefert ein
 * sicheres Default (Center, 1.0x) wenn nichts gesetzt.
 */
export function parseImagePosition(raw: unknown): ImagePosition {
  if (!raw || typeof raw !== 'object') return DEFAULT_POS
  const r = raw as Record<string, unknown>
  const zoom = typeof r.zoom === 'number' ? Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, r.zoom)) : 1
  const x = typeof r.x === 'number' ? Math.max(-50, Math.min(50, r.x)) : 0
  const y = typeof r.y === 'number' ? Math.max(-50, Math.min(50, r.y)) : 0
  return { zoom, x, y }
}

/**
 * Generiert CSS-Style für ein <img>-Tag mit object-cover damit die Position
 * korrekt angewandt wird (transform + transform-origin).
 */
export function imageStyleFromPosition(pos: ImagePosition | null | undefined): React.CSSProperties {
  if (!pos || (pos.zoom === 1 && pos.x === 0 && pos.y === 0)) return {}
  return {
    transform: `translate(${pos.x}%, ${pos.y}%) scale(${pos.zoom})`,
    transformOrigin: 'center center',
  }
}
